import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Spinner,
  cn,
} from '@lensora/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'


import { useBillingActivation } from '@/hooks/use-billing-activation'
import { useSubscription } from '@/hooks/use-subscription'
import { api } from '@/lib/api'
import {
  fetchBillingStatus,
  fetchPlans,
  startCheckout,
  type PublicPlan,
} from '@/lib/billing'

const TIER_ORDER = ['starter', 'growth', 'enterprise']

const TIER_COPY: Record<string, { blurb: string; price: Record<string, string> }> = {
  starter: { blurb: 'Track up to 3 websites with monthly reports', price: { month: '£49/mo', year: '£39/mo (billed annually)' } },
  growth: { blurb: 'Unlimited websites with weekly reports and insights', price: { month: '£149/mo', year: '£119/mo (billed annually)' } },
  enterprise: { blurb: 'For large organisations with custom needs', price: { month: '£499/mo', year: '£399/mo (billed annually)' } },
}

export function BillingPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const checkoutResult = searchParams.get('checkout')
  const { data: subscription, isLoading: subLoading } = useSubscription()

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['billing-plans'],
    queryFn: fetchPlans,
    staleTime: 1000 * 60 * 10,
  })

  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ['billing-status'],
    queryFn: fetchBillingStatus,
  })

  // After returning from Checkout, the subscription lands via webhook — poll
  // briefly until it shows up instead of flashing "no subscription".
  const initialCheckoutResult = React.useRef(checkoutResult).current
  const checkoutActivation = useBillingActivation({
    refetchStatus,
    isDone: (data) => !!data?.hasSubscription,
    maxAttempts: 15,
    onSettled: () => {
      setSearchParams({}, { replace: true })
      void queryClient.invalidateQueries({ queryKey: ['subscription'] })
      void queryClient.invalidateQueries({ queryKey: ['billing-status'] })
    },
  })
  React.useEffect(() => {
    if (initialCheckoutResult === 'success') checkoutActivation.start()
    // Only ever fires once, for the checkout redirect this page loaded with.
  }, [initialCheckoutResult, checkoutActivation.start])

  // Switching plans also lands via webhook — poll until the new tier is
  // resolved rather than invalidating immediately, which can flash the old
  // plan tier back before the DB has caught up.
  const targetTierRef = React.useRef<string | null>(null)
  const changeActivation = useBillingActivation({
    refetchStatus,
    isDone: (data) => !!data?.planResolved && data?.planTier === targetTierRef.current,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscription'] })
      void queryClient.invalidateQueries({ queryKey: ['billing-status'] })
    },
  })

  const portalMutation = useMutation({
    mutationFn: () =>
      api.post<{ url: string }>('/api/billing/portal', {
        returnUrl: window.location.href,
      }),
    onSuccess: ({ url }) => {
      window.location.href = url
    },
  })

  const checkoutMutation = useMutation({
    mutationFn: (priceId: string) => startCheckout(priceId),
  })

  const changePlanMutation = useMutation({
    mutationFn: (plan: PublicPlan) =>
      api.post('/api/billing/change-plan', { priceId: plan.priceId }),
    onSuccess: (_data, plan) => {
      targetTierRef.current = plan.tier
      changeActivation.start()
    },
  })

  const hasSubscription = status?.hasSubscription ?? false
  const currentPlanId = subscription?.planId ?? null

  // One card per tier; prefer the monthly price row
  const tierPlans = React.useMemo(() => {
    const byTier = new Map<string, PublicPlan[]>()
    for (const p of plans ?? []) {
      if (!p.tier) continue
      const list = byTier.get(p.tier) ?? []
      list.push(p)
      byTier.set(p.tier, list)
    }
    return TIER_ORDER.filter((t) => byTier.has(t)).map((t) => {
      const list = byTier.get(t)!
      return list.find((p) => p.interval === 'month') ?? list[0]!
    })
  }, [plans])

  if (subLoading || plansLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-text">Billing</h2>
        <p className="mt-1 text-sm text-text-muted">Manage your subscription and invoices.</p>
      </div>

      {checkoutActivation.activating && (
        <Card>
          <CardContent className="flex items-center gap-3 py-6">
            <Spinner />
            <div>
              <p className="font-medium text-text">Activating your subscription…</p>
              <p className="text-sm text-text-muted">
                Finalising things with Stripe. This normally takes a few seconds.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {checkoutActivation.timedOut && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-feedback-warning">
              This is taking longer than usual — please refresh in a moment.
            </p>
          </CardContent>
        </Card>
      )}

      {changeActivation.activating && (
        <Card>
          <CardContent className="flex items-center gap-3 py-6">
            <Spinner />
            <div>
              <p className="font-medium text-text">Switching your plan…</p>
              <p className="text-sm text-text-muted">
                Confirming the change with Stripe. This normally takes a few seconds.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {changeActivation.timedOut && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-feedback-warning">
              This is taking longer than usual to confirm — please refresh in a moment.
            </p>
          </CardContent>
        </Card>
      )}

      {checkoutResult === 'cancelled' && !hasSubscription && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-text-muted">
              Checkout was cancelled — pick a plan below whenever you're ready.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Current plan</CardTitle>
          <CardDescription>Your subscription details.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {subscription ? (
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                {status?.planResolved === false ? (
                  <p className="font-medium text-feedback-warning">
                    We're having trouble confirming your plan — contact support
                  </p>
                ) : (
                  <p className="font-medium text-text capitalize">
                    {status?.planTier ?? 'Active'} plan
                  </p>
                )}
                <p className="text-sm text-text-muted">
                  {subscription.status === 'trialing' ? 'Trial converts' : 'Renews'}{' '}
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </p>
              </div>
              <Badge
                variant={
                  subscription.status === 'active' || subscription.status === 'trialing'
                    ? 'success'
                    : 'warning'
                }
                className="capitalize"
              >
                {subscription.status === 'trialing'
                  ? 'Free trial'
                  : subscription.status.replace('_', ' ')}
              </Badge>
            </div>
          ) : (
            <p className="text-sm text-text-muted">
              No active subscription — choose a plan below to get started.
            </p>
          )}
        </CardContent>
        {subscription && (
          <CardFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
            >
              {portalMutation.isPending ? 'Redirecting…' : 'Manage billing'}
            </Button>
          </CardFooter>
        )}
      </Card>

      {tierPlans.length > 0 && status?.planTier !== 'custom' && (
        <div>
          <h3 className="mb-3 text-lg font-semibold text-text">
            {hasSubscription ? 'Change plan' : 'Choose a plan'}
          </h3>
          <div className="grid gap-4 md:grid-cols-3">
            {tierPlans.map((plan) => {
              const isCurrent = currentPlanId === plan.id
              const copy = plan.tier ? TIER_COPY[plan.tier] : undefined
              return (
                <Card
                  key={plan.id}
                  className={cn(isCurrent && 'border-brand-accent')}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{plan.name}</CardTitle>
                      {isCurrent && <Badge variant="success">Current</Badge>}
                    </div>
                    <CardDescription>
                      {copy?.price[plan.interval] ?? ''}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-text-muted">{copy?.blurb}</p>
                    <p className="mt-2 text-sm font-medium text-text">
                      {plan.creditsPerMonth > 0
                        ? `${plan.creditsPerMonth.toLocaleString()} credits/mo`
                        : ''}
                    </p>
                  </CardContent>
                  <CardFooter>
                    {isCurrent ? (
                      <Button variant="outline" size="sm" disabled className="w-full">
                        Your plan
                      </Button>
                    ) : hasSubscription ? (
                      <Button
                        size="sm"
                        className="w-full"
                        disabled={changePlanMutation.isPending || changeActivation.activating}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Switch to the ${plan.name} plan? The price difference is prorated and invoiced immediately.`
                            )
                          ) {
                            changePlanMutation.mutate(plan)
                          }
                        }}
                      >
                        {(changePlanMutation.isPending || changeActivation.activating) &&
                        changePlanMutation.variables?.priceId === plan.priceId
                          ? 'Switching…'
                          : `Switch to ${plan.name}`}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="w-full"
                        disabled={checkoutMutation.isPending}
                        onClick={() => checkoutMutation.mutate(plan.priceId)}
                      >
                        {checkoutMutation.isPending && checkoutMutation.variables === plan.priceId
                          ? 'Redirecting…'
                          : 'Start free trial'}
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              )
            })}
          </div>
          {(changePlanMutation.isError || checkoutMutation.isError) && (
            <p className="mt-3 text-sm text-feedback-error">
              Something went wrong — please try again or contact support.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
